/**
 *  @class
 *  @function Timeline
 */

if (!customElements.get('time-line')) {
	class Timeline extends HTMLElement {
		constructor() {
			super();
			this.id = this.dataset.id;
			this.scroller = this.querySelector('.timeline--scroll-container');
			this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
			this.scrollSet = false;
			this.items = this.querySelectorAll('.timeline--slide');
			this.labels = this.querySelectorAll('.timeline--scroll-pagination li');
			this.scrollbarThumb = this.querySelector('.timeline--scrollbar-thumb');
			this.prevBtn = this.querySelector('.timeline--nav-prev');
			this.nextBtn = this.querySelector('.timeline--nav-next');
			this._slideIndex = 0;
			this._navigating = false;

			if (this.animations_enabled) {
				this.scrollSet = gsap.quickSetter('#time-line-progress-' + this.id, "scaleX");
			}
		}
		connectedCallback() {
			const _this = this;

			if (this.animations_enabled) {
				_this.scroller.addEventListener('scroll', () => this.onScroll());
			}

			// Scrollbar + edge state — always active
			_this.scroller.addEventListener('scroll', () => this.updateScrollbar());
			window.addEventListener('resize', () => this.updateScrollbar());
			requestAnimationFrame(() => this.updateScrollbar());

			if (this.scrollbarThumb) {
				this.initScrollbarDrag();
			}

			if (this.prevBtn) {
				this.prevBtn.addEventListener('click', () => this.navigateSlide(-1));
				this.nextBtn.addEventListener('click', () => this.navigateSlide(1));
			}

			_this.labels.forEach((item) => {
				_this.setLabelPosition(item);
			});
		}
		navigateSlide(direction) {
			const slides = Array.from(this.items);
			if (!slides.length) return;
			const targetIndex = Math.max(0, Math.min(slides.length - 1, this._slideIndex + direction));
			this._slideIndex = targetIndex;
			Array.from(this.labels).forEach((label, i) => {
				label.classList.toggle('is-active', i === targetIndex);
			});
			this._navigating = true;
			clearTimeout(this._navTimeout);

			const confirmNav = () => {
				if (!this._navigating) return;
				this._navigating = false;
				clearTimeout(this._navTimeout);
				this.scroller.removeEventListener('scrollend', confirmNav);
				Array.from(this.labels).forEach((label, i) => {
					label.classList.toggle('is-active', i === this._slideIndex);
				});
			};

			this.scroller.addEventListener('scrollend', confirmNav);
			this._navTimeout = setTimeout(confirmNav, 1000);

			const firstLeft = slides[0].offsetLeft;
			this.scroller.scrollTo({ left: slides[targetIndex].offsetLeft - firstLeft, behavior: 'smooth' });
		}
		updateScrollEdges() {
			const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
			this.classList.toggle('at-start', this.scroller.scrollLeft <= 1);
			this.classList.toggle('at-end', maxScroll <= 0 || this.scroller.scrollLeft >= maxScroll - 1);
		}
		updateActiveDot() {
			if (this._navigating) return;
			const slides = Array.from(this.items);
			const labels = Array.from(this.labels);
			if (!slides.length || !labels.length) return;
			const scrollLeft = this.scroller.scrollLeft;
			const firstLeft = slides[0].offsetLeft;
			let activeIndex = 0;
			let minDist = Infinity;
			slides.forEach((slide, i) => {
				const dist = Math.abs((slide.offsetLeft - firstLeft) - scrollLeft);
				if (dist < minDist) {
					minDist = dist;
					activeIndex = i;
				}
			});
			const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
			if (maxScroll > 0 && scrollLeft >= maxScroll - 50 && activeIndex < this._slideIndex) {
				activeIndex = this._slideIndex;
			}
			this._slideIndex = activeIndex;
			labels.forEach((label, i) => {
				label.classList.toggle('is-active', i === activeIndex);
			});
		}
		updateScrollbar() {
			this.updateScrollEdges();
			this.updateActiveDot();
			if (!this.scrollbarThumb) return;
			const contentWidth = this.scroller.scrollWidth;
			const visibleWidth = this.scroller.offsetWidth;
			const thumbWidth = (visibleWidth / contentWidth) * 100;
			const maxScroll = contentWidth - visibleWidth;
			const scrollFraction = maxScroll > 0 ? this.scroller.scrollLeft / maxScroll : 0;
			const thumbLeft = scrollFraction * (100 - thumbWidth);
			this.scrollbarThumb.style.width = thumbWidth + '%';
			this.scrollbarThumb.style.left = thumbLeft + '%';
		}
		initScrollbarDrag() {
			const thumb = this.scrollbarThumb;
			const track = thumb.parentElement;
			let isDragging = false;
			let startX = 0;
			let startScrollLeft = 0;

			const onStart = (x) => {
				isDragging = true;
				startX = x;
				startScrollLeft = this.scroller.scrollLeft;
			};
			const onMove = (x) => {
				if (!isDragging) return;
				const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
				const scrollRatio = maxScroll / (track.offsetWidth - thumb.offsetWidth);
				this.scroller.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + (x - startX) * scrollRatio));
			};
			const onEnd = () => { isDragging = false; };

			thumb.addEventListener('mousedown', (e) => { onStart(e.clientX); e.preventDefault(); });
			document.addEventListener('mousemove', (e) => onMove(e.clientX));
			document.addEventListener('mouseup', onEnd);

			thumb.addEventListener('touchstart', (e) => { onStart(e.touches[0].clientX); }, { passive: true });
			document.addEventListener('touchmove', (e) => { if (isDragging) onMove(e.touches[0].clientX); }, { passive: true });
			document.addEventListener('touchend', onEnd);

			// Click on track to jump
			track.addEventListener('click', (e) => {
				if (e.target === thumb) return;
				const rect = track.getBoundingClientRect();
				const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
				const fraction = (e.clientX - rect.left - thumb.offsetWidth / 2) / (track.offsetWidth - thumb.offsetWidth);
				this.scroller.scrollLeft = Math.max(0, Math.min(maxScroll, fraction * maxScroll));
			});
		}
		setLabelPosition(item) {
			let contentWidth = this.scroller.querySelector('.timeline--scroll-inner').offsetWidth,
				arr = Array.prototype.slice.call(this.labels),
				i = arr.indexOf(item),
				slide = this.items[i],
				nextSlide = this.items[i + 1],
				slotWidth = nextSlide
					? nextSlide.offsetLeft - slide.offsetLeft
					: slide.offsetWidth;

			item.style.flex = (slotWidth / contentWidth);
		}
		onScroll() {
			let scrollLeft = this.scroller.scrollLeft,
				scrollerWidth = this.scroller.offsetWidth,
				contentWidth = this.scroller.querySelector('.timeline--scroll-inner').offsetWidth,
				scrollPercent = (scrollLeft) / (contentWidth - scrollerWidth);

			this.scrollSet(scrollPercent);
		}
	}
	customElements.define('time-line', Timeline);
}
